import React, { Component, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import server from '../../../api/server';
import { connect } from 'react-redux';
import { setAuth } from '../../../actions';

/**
 * Modal for previewing and confirming subscription changes
 */
class SubscriptionChangeModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            error: null,
            previewData: null,
            processing: false,
            open: true
        };
    }

    componentDidMount() {
        this.fetchChangePreview();
    }

    /**
     * Fetch preview data for the subscription change
     */
    fetchChangePreview = async () => {
        this.setState({ loading: true, error: null });

        try {
            const { user_id, targetPlan, targetBilling } = this.props;

            const response = await server.post('/dashboard/change-subscription/preview', {
                user_id,
                targetPlan,
                targetBilling
            });

            if (response.data.success) {
                this.setState({
                    previewData: response.data,
                    loading: false
                });
            } else {
                throw new Error(response.data.message || 'Failed to load preview');
            }
        } catch (error) {
            console.error('Error fetching subscription preview:', error);
            this.setState({
                error: error.message || 'Failed to load preview data',
                loading: false
            });
        }
    };

    /**
     * Handle confirming the subscription change
     */
    handleConfirmChange = async () => {
        this.setState({ processing: true, error: null });

        try {
            const { user_id, targetPlan, targetBilling } = this.props;

            const response = await server.post('/dashboard/change-subscription', {
                user_id,
                targetPlan,
                targetBilling
            });

            if (response.data.success) {
                // Update user data in Redux store
                if (this.props.setAuth) {
                    this.props.setAuth({
                        ...this.props.auth,
                        user: {
                            ...this.props.user,
                            premium_type: targetPlan,
                            premium_billing_cycle: targetBilling
                        }
                    });
                }

                this.setState({ open: false });

                // Redirect to premium upgrade processing screen
                window.location.href = '/dashboard/premium-upgrade-processing';

                // Call onSuccess callback if needed
                if (this.props.onSuccess) {
                    this.props.onSuccess(response.data);
                }
            } else {
                throw new Error(response.data.message || 'Failed to change subscription');
            }
        } catch (error) {
            console.error('Error changing subscription:', error);
            this.setState({
                error: error.message || 'Failed to change subscription',
                processing: false
            });
        }
    };

    closeModal = () => {
        this.setState({ open: false });
        this.props.onClose();
    };

    render() {
        const { loading, error, previewData, processing, open } = this.state;
        const { targetPlan, targetBilling } = this.props;

        // Helper functions for displaying plan info
        const getPlanName = plan => plan === 'single' ? 'Single Bot' : 'Unlimited Bots';
        const getBillingName = cycle => cycle === 'monthly' ? 'Monthly' : 'Yearly';

        return (
            <Transition appear show={open} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-[9999]"
                    onClose={this.closeModal}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-[#1a1c1f]/90 z-[9999]" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto z-[9999]">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-[#2f3136] text-left align-middle shadow-xl transition-all border border-[#1a1c1f]">
                                    {/* Header */}
                                    <div className="px-6 py-4 bg-[#202225] border-b border-[#36393f] flex justify-between items-center">
                                        <Dialog.Title
                                            as="h3"
                                            className="text-xl font-bold text-white"
                                        >
                                            Subscription Change
                                        </Dialog.Title>
                                        <button
                                            onClick={this.closeModal}
                                            className="text-[#b9bbbe] hover:text-white focus:outline-none"
                                        >
                                            <svg
                                                className="h-5 w-5"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M6 18L18 6M6 6l12 12"
                                                />
                                            </svg>
                                        </button>
                                    </div>

                                    {/* Body */}
                                    <div className="px-6 py-4">
                                        {loading ? (
                                            <div className="flex justify-center items-center h-40">
                                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FF4444]"></div>
                                            </div>
                                        ) : error ? (
                                            <div className="text-center py-6">
                                                <div className="text-[#FF4444] text-lg mb-2">Error</div>
                                                <p className="text-[#b9bbbe]">{error}</p>
                                                <button
                                                    onClick={this.closeModal}
                                                    className="mt-4 px-4 py-2 bg-[#4f545c] hover:bg-[#5d6269] text-white rounded-md transition-colors"
                                                >
                                                    Close
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="mb-6">
                                                    <p className="text-[#b9bbbe] mb-4">
                                                        You are changing from <span className="text-white font-semibold">
                                                            {getPlanName(previewData.currentPlan.type)} ({getBillingName(previewData.currentPlan.billing)})
                                                        </span> to <span className="text-white font-semibold">
                                                            {getPlanName(previewData.targetPlan.type)} ({getBillingName(previewData.targetPlan.billing)})
                                                        </span>
                                                    </p>

                                                    {/* Current Plan Card */}
                                                    <div className="bg-[#202225] p-4 rounded-md mb-4">
                                                        <div className="text-sm text-[#b9bbbe] mb-1">Current Subscription</div>
                                                        <div className="flex justify-between">
                                                            <div className="text-white">{getPlanName(previewData.currentPlan.type)}</div>
                                                            <div className="text-white">{previewData.currentPlan.formattedPrice}</div>
                                                        </div>
                                                    </div>

                                                    {/* New Plan Card */}
                                                    <div className="bg-[#202225] p-4 rounded-md mb-4">
                                                        <div className="text-sm text-[#b9bbbe] mb-1">New Subscription</div>
                                                        <div className="flex justify-between">
                                                            <div className="text-white">{getPlanName(previewData.targetPlan.type)}</div>
                                                            <div className="text-right">
                                                                {previewData.targetPlan.originalPrice && (
                                                                    <div className="text-[#b9bbbe] line-through text-sm">
                                                                        ${previewData.targetPlan.originalPrice}/{previewData.targetPlan.billing === 'yearly' ? 'year' : 'month'}
                                                                    </div>
                                                                )}
                                                                <div className="text-white">{previewData.targetPlan.formattedPrice}</div>
                                                            </div>
                                                        </div>

                                                        {/* Sale badge if applicable */}
                                                        {previewData.targetPlan.discountPercent && (
                                                            <div className="flex justify-end mt-1">
                                                                <div className="text-xs bg-[#FFD700] text-black px-2 py-0.5 rounded-full font-medium">
                                                                    {previewData.targetPlan.discountPercent}% OFF
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Sale notice if applicable */}
                                                        {previewData.sale && (
                                                            <div className="flex items-center mt-2 border-t border-[#36393f] pt-2">
                                                                <div className="w-3 h-3 mr-2 bg-[#FFD700] flex-shrink-0 rounded-full"></div>
                                                                <div className="text-[#FFD700] text-xs">
                                                                    Sale discount applied: {previewData.sale.percent}% off
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Billing Details Card */}
                                                    <div className="bg-[#202225] p-4 rounded-md">
                                                        <div className="text-sm text-[#b9bbbe] mb-2">Payment Summary</div>

                                                        <div className="space-y-2">
                                                            {parseFloat(previewData.billing.remainingCredit) > 0 && (
                                                                <div className="flex justify-between">
                                                                    <div className="text-[#b9bbbe]">Remaining credit</div>
                                                                    <div className="text-[#43b581]">${previewData.billing.remainingCredit}</div>
                                                                </div>
                                                            )}

                                                            {parseFloat(previewData.billing.immediateCharge) > 0 && (
                                                                <div className="flex justify-between">
                                                                    <div className="text-[#b9bbbe]">Immediate charge</div>
                                                                    <div className="text-[#FF4444]">${previewData.billing.immediateCharge}</div>
                                                                </div>
                                                            )}

                                                            {parseFloat(previewData.billing.creditToNextBill) > 0 && (
                                                                <div className="flex justify-between">
                                                                    <div className="text-[#b9bbbe]">Credit to next bill</div>
                                                                    <div className="text-[#43b581]">${previewData.billing.creditToNextBill}</div>
                                                                </div>
                                                            )}

                                                            {/* If there's nothing to show, display a simple message */}
                                                            {parseFloat(previewData.billing.remainingCredit) <= 0 &&
                                                                parseFloat(previewData.billing.immediateCharge) <= 0 &&
                                                                parseFloat(previewData.billing.creditToNextBill) <= 0 && (
                                                                    <div className="text-center text-[#b9bbbe] py-1">
                                                                        No immediate billing changes
                                                                    </div>
                                                                )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Warning for downgrades */}
                                                {previewData.currentPlan.type === 'unlimited' && previewData.targetPlan.type === 'single' && (
                                                    <div className="bg-[#35312f] border border-[#e24444] p-4 rounded-md mb-4">
                                                        <div className="text-[#e24444] font-bold mb-1">Warning</div>
                                                        <p className="text-[#b9bbbe] text-sm">
                                                            Downgrading from Unlimited to Single will limit premium features to only one bot.
                                                        </p>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {/* Footer */}
                                    {!loading && !error && (
                                        <div className="bg-[#202225] px-6 py-4 border-t border-[#36393f] flex justify-end space-x-3">
                                            <button
                                                onClick={this.closeModal}
                                                className="px-4 py-2 bg-[#4f545c] hover:bg-[#5d6269] text-white rounded-md transition-colors"
                                                disabled={processing}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={this.handleConfirmChange}
                                                disabled={processing}
                                                className="px-4 py-2 bg-[#ff4444] hover:bg-[#ff6666] text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                            >
                                                {processing ? (
                                                    <>
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                        Processing...
                                                    </>
                                                ) : "Confirm Change"}
                                            </button>
                                        </div>
                                    )}
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        );
    }
}

const mapStateToProps = (state) => ({
    auth: state.auth,
    user: state.data.user
});

export default connect(mapStateToProps, { setAuth })(SubscriptionChangeModal); 